import { useEffect, useRef, useState } from "react";

const VolumeReactiveVisualizer = ({ mediaRecorder, width = "100%", height = 200, barColor = "grey" }) => {
    const [leftTimelineData, setLeftTimelineData] = useState(Array(40).fill(0));
    const [rightTimelineData, setRightTimelineData] = useState(Array(40).fill(0));
    const analyzerRef = useRef(null);
    const audioContextRef = useRef(null);
    const sourceRef = useRef(null);
    const animationRef = useRef(null);
    const frameCounter = useRef(0);
  
    useEffect(() => {
      if (mediaRecorder && mediaRecorder.state === 'recording') {
        startTimelineDetection();
      } else {
        stopTimelineDetection();
      }
  
      return () => stopTimelineDetection();
    }, [mediaRecorder?.state]);
  
    const startTimelineDetection = async () => {
      try {
        // Clean up any existing context first
        if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
          audioContextRef.current.close();
        }
  
        audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
        sourceRef.current = audioContextRef.current.createMediaStreamSource(mediaRecorder.stream);
        analyzerRef.current = audioContextRef.current.createAnalyser();
        
        analyzerRef.current.fftSize = 256;
        analyzerRef.current.smoothingTimeConstant = 0.3;
        
        sourceRef.current.connect(analyzerRef.current);
        
        processTimelineAudio();
      } catch (error) {
        console.error('Timeline audio detection failed:', error);
      }
    };
  
    const processTimelineAudio = () => {
      if (!analyzerRef.current) return;
  
      const dataArray = new Uint8Array(analyzerRef.current.frequencyBinCount);
      
      const updateTimeline = () => {
        analyzerRef.current.getByteFrequencyData(dataArray);
        
        frameCounter.current++;
        
        if (frameCounter.current >= 4) {
          frameCounter.current = 0;
          
          let sum = 0;
          for (let i = 0; i < 32; i++) {
            sum += dataArray[i] * dataArray[i];
          }
          const rms = Math.sqrt(sum / 32);
          const currentAmplitude = rms / 255;
          
          const threshold = 0.03;
          const amplitudeValue = currentAmplitude > threshold ? currentAmplitude * 150 : 0;
          
          setLeftTimelineData(prev => {
            const newTimeline = [...prev];
            for (let i = 0; i < newTimeline.length - 1; i++) {
              newTimeline[i] = newTimeline[i + 1];
            }
            newTimeline[newTimeline.length - 1] = amplitudeValue;
            return newTimeline;
          });
  
          setRightTimelineData(prev => {
            const newTimeline = [...prev];
            for (let i = 0; i < newTimeline.length - 1; i++) {
              newTimeline[i] = newTimeline[i + 1];
            }
            newTimeline[newTimeline.length - 1] = 0;
            return newTimeline;
          });
        }
  
        if (mediaRecorder && mediaRecorder.state === 'recording') {
          animationRef.current = requestAnimationFrame(updateTimeline);
        }
      };
  
      updateTimeline();
    };
  
    // **FIXED** stopTimelineDetection function
    const stopTimelineDetection = () => {
      // Stop animation frame
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
        animationRef.current = null;
      }
  
      // Disconnect audio source
      if (sourceRef.current) {
        try {
          sourceRef.current.disconnect();
          sourceRef.current = null;
        } catch (e) {
          // Already disconnected
        }
      }
  
      // **KEY FIX**: Close audio context only if it exists and isn't already closed
      if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
        try {
          audioContextRef.current.close();
        } catch (e) {
          console.error('Error closing audio context:', e);
        }
      }
      
      // Set to null after closing
      audioContextRef.current = null;
      analyzerRef.current = null;
  
      // Reset state
      setLeftTimelineData(Array(40).fill(0));
      setRightTimelineData(Array(40).fill(0));
      frameCounter.current = 0;
    };
  
    return (
      <div 
        style={{ 
          width: width, 
          height: `${height}px`, 
          display: 'flex', 
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'transparent',
          position: 'relative',
          padding: '20px 10px'
        }}
      >
        <div 
          style={{
            display: 'flex',
            alignItems: 'center',
            height: '100%',
            width: '100%',
            position: 'relative'
          }}
        >
          {/* LEFT SIDE - Past audio data */}
          <div 
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              width: '50%',
              height: '100%'
            }}
          >
            {leftTimelineData.map((amplitude, index) => {
              const barHeight = Math.max(amplitude, 0);
              const isSound = barHeight > 8;
              const opacity = Math.max(0.4 + (index / leftTimelineData.length) * 0.6, 0.4);
              
              return (
                <div
                  key={`left-${index}`}
                  style={{
                    width: '2px',
                    height: isSound ? `${Math.min(barHeight, 120)}px` : '2px',
                    backgroundColor: isSound ? barColor : '#ddd',
                    borderRadius: '1px',
                    opacity: opacity,
                    transition: 'height 0.05s ease-out',
                    minHeight: '2px'
                  }}
                />
              );
            })}
          </div>
  
          {/* RED CENTER LINE */}
          <div
            style={{
              width: '2px',
              height: '120px',
              backgroundColor: 'red',
              borderRadius: '1px',
              zIndex: 10,
              margin: '0 2px'
            }}
          />
  
          {/* RIGHT SIDE - Future baseline */}
          <div 
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              width: '50%',
              height: '100%'
            }}
          >
            {rightTimelineData.map((_, index) => {
              const opacity = Math.max(1 - (index / rightTimelineData.length) * 0.5, 0.3);
              
              return (
                <div
                  key={`right-${index}`}
                  style={{
                    width: '2px',
                    height: '2px',
                    backgroundColor: '#ddd',
                    borderRadius: '1px',
                    opacity: opacity,
                    minHeight: '2px'
                  }}
                />
              );
            })}
          </div>
        </div>
        
        {/* Horizontal baseline */}
        <div 
          style={{
            position: 'absolute',
            width: '100%',
            height: '1px',
            backgroundColor: '#e0e0e0',
            top: '50%',
            transform: 'translateY(-50%)',
            zIndex: 0,
            opacity: 0.5
          }}
        />
      </div>
    );
  };

export default VolumeReactiveVisualizer;
